import { Attachment } from 'Src/models/generalMessage';

export const DEFAULT_GENERAL_MESSAGE_ATTACHMENT_SIZE_LIMIT_BYTES = 10 * 1024 * 1024;

export interface AttachmentSizeValidationResult {
  isValid: boolean;
  totalBytes: number;
  limitBytes: number;
  errorMessage?: string;
}

function readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
  if (typeof file.arrayBuffer === 'function') {
    return file.arrayBuffer();
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(reader.error || new Error('The attachment could not be read.'));
    reader.readAsArrayBuffer(file);
  });
}

function toHex(bytes: Uint8Array): string {
  return Array.from(bytes)
    .map(value => (value + 256).toString(16).slice(-2))
    .join('');
}

export async function getGeneralMessageAttachmentContentHash(file: File): Promise<string> {
  const buffer = await readFileAsArrayBuffer(file);
  const cryptoApi = typeof window !== 'undefined' ? window.crypto : undefined;

  if (cryptoApi && cryptoApi.subtle) {
    const digest = await cryptoApi.subtle.digest('SHA-256', buffer);
    return `sha256:${toHex(new Uint8Array(digest))}`;
  }

  // Deterministic fallback for older embedded browsers. It is used only for
  // duplicate detection in one message, not as a security primitive.
  let hash = 2166136261;
  const bytes = new Uint8Array(buffer);
  for (let index = 0; index < bytes.length; index += 1) {
    hash ^= bytes[index];
    hash = Math.imul(hash, 16777619);
  }
  return `fnv1a:${(hash >>> 0).toString(16)}:${bytes.length}`;
}

export function isGeneralMessageAttachmentDuplicate(attachments: Attachment[], candidate: Attachment): boolean {
  return (attachments || []).some(attachment => {
    if (candidate.contentHash && attachment.contentHash) {
      return candidate.contentHash === attachment.contentHash;
    }

    return Boolean(
      candidate.externalFileId && attachment.externalFileId && candidate.externalFileId === attachment.externalFileId
    );
  });
}

export function getGeneralMessageDuplicateAttachmentError(fileName: string): string {
  return `“${fileName}” is already attached to this message.`;
}

export function normalizeGeneralMessageAttachmentSizeLimit(configuredLimit: unknown): number {
  const parsedLimit = typeof configuredLimit === 'number' ? configuredLimit : Number(configuredLimit);

  return Number.isSafeInteger(parsedLimit) && parsedLimit > 0
    ? parsedLimit
    : DEFAULT_GENERAL_MESSAGE_ATTACHMENT_SIZE_LIMIT_BYTES;
}

export function formatGeneralMessageAttachmentSize(bytes: number): string {
  const safeBytes = normalizeGeneralMessageAttachmentSizeLimit(bytes);

  if (safeBytes < 1024) {
    return `${safeBytes} B`;
  }

  if (safeBytes < 1024 * 1024) {
    const sizeInKb = Math.round((safeBytes / 1024) * 100) / 100;
    return `${sizeInKb.toLocaleString('en-GB', { maximumFractionDigits: 2 })} KB`;
  }

  const sizeInMb = safeBytes / (1024 * 1024);
  const roundedSize = Math.round(sizeInMb * 100) / 100;

  return `${roundedSize.toLocaleString('en-GB', { maximumFractionDigits: 2 })} MB`;
}

export function validateGeneralMessageAttachmentCollection(
  attachments: Attachment[],
  configuredLimit: unknown
): AttachmentSizeValidationResult {
  const limitBytes = normalizeGeneralMessageAttachmentSizeLimit(configuredLimit);
  let totalBytes = 0;

  for (const attachment of attachments || []) {
    const fileSize = attachment && attachment.fileSize;

    if (!Number.isSafeInteger(fileSize) || (fileSize as number) < 0) {
      return {
        isValid: false,
        totalBytes,
        limitBytes,
        errorMessage:
          'The size of an existing attachment could not be verified. Remove that attachment and attach the file again.'
      };
    }

    if (!Number.isSafeInteger(totalBytes + (fileSize as number))) {
      return {
        isValid: false,
        totalBytes,
        limitBytes,
        errorMessage: 'The total attachment size could not be calculated. Remove the attachments and try again.'
      };
    }

    totalBytes += fileSize as number;
  }

  if (totalBytes > limitBytes) {
    return {
      isValid: false,
      totalBytes,
      limitBytes,
      errorMessage: `The total size of the attachments exceeds ${formatGeneralMessageAttachmentSize(
        limitBytes
      )}. Remove one or more attachments and try again.`
    };
  }

  return { isValid: true, totalBytes, limitBytes };
}

export function validateGeneralMessageAttachmentAddition(
  attachments: Attachment[],
  fileSize: number,
  configuredLimit: unknown
): AttachmentSizeValidationResult {
  if (!Number.isSafeInteger(fileSize) || fileSize < 0) {
    return {
      isValid: false,
      totalBytes: 0,
      limitBytes: normalizeGeneralMessageAttachmentSizeLimit(configuredLimit),
      errorMessage: 'This file cannot be attached because its size could not be determined.'
    };
  }

  const validation = validateGeneralMessageAttachmentCollection(
    [...(attachments || []), { fileName: '', externalFileId: '', fileSize }],
    configuredLimit
  );

  if (!validation.isValid && validation.totalBytes > validation.limitBytes) {
    return {
      ...validation,
      errorMessage: `This file cannot be attached because the total size of the attachments would exceed ${formatGeneralMessageAttachmentSize(
        validation.limitBytes
      )}.`
    };
  }

  return validation;
}

export function getGeneralMessageAttachmentUploadError(error: any, configuredLimit: unknown): string {
  const status = error && error.response && error.response.status;
  const detailsText = error && error.response && error.response.data && error.response.data.details;
  const serverMessage = detailsText && typeof detailsText.text === 'string' ? detailsText.text : '';

  if (status === 413) {
    return `The file upload was rejected because the file is too large. The General messages attachment limit is ${formatGeneralMessageAttachmentSize(
      normalizeGeneralMessageAttachmentSizeLimit(configuredLimit)
    )}; the file service may enforce an additional upload limit.`;
  }

  if (serverMessage) {
    return `The attachment could not be uploaded. ${serverMessage}`;
  }

  return 'The attachment could not be uploaded. Check your connection and try again.';
}

export function getGeneralMessageRequestError(error: any, fallbackMessage: string): string {
  const detailsText = error && error.response && error.response.data && error.response.data.details;
  const serverMessage = detailsText && typeof detailsText.text === 'string' ? detailsText.text : '';

  if (serverMessage) return serverMessage;
  if (error && typeof error.message === 'string' && error.message) return error.message;

  return fallbackMessage;
}
